import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GraphUser } from "../../../../../../utils/MsGraphApiCall";
import { useCreateProfileMutation } from "../../../../../../services/ProfileService";
import { useUserSearch } from "./useUserSearch";

interface UseProfileCreateDialogProps {
    onProfileCreated?: (identity: string) => void;
}

interface UseProfileCreateDialogResult {
    // Dialog state
    open: boolean;
    openDialog: () => void;
    closeDialog: () => void;
    
    // Search state and actions
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    searchResults: GraphUser[];
    isSearching: boolean;
    handleSearch: () => Promise<void>;
    
    // Selection state
    selectedUser: GraphUser | null;
    handleUserSelect: (user: GraphUser) => void;
    
    // Error state
    error: string | null;
    clearError: () => void;
    
    // Create action
    isCreating: boolean;
    handleCreateProfile: () => Promise<void>;
    
    // Keyboard handler
    handleKeyPress: (event: React.KeyboardEvent) => void;
}

export const useProfileCreateDialog = ({
    onProfileCreated
}: UseProfileCreateDialogProps): UseProfileCreateDialogResult => {
    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);

    const {
        searchTerm,
        setSearchTerm,
        searchResults,
        isSearching,
        searchError,
        clearSearchError,
        handleSearch,
        resetSearch
    } = useUserSearch();

    const [createProfile, { isLoading: isCreating }] = useCreateProfileMutation();
    const navigate = useNavigate();

    const error = searchError || createError;

    const clearError = useCallback(() => {
        clearSearchError();
        setCreateError(null);
    }, [clearSearchError]);

    const openDialog = useCallback(() => {
        setOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setOpen(false);
        resetSearch();
        setSelectedUser(null);
        setCreateError(null);
    }, [resetSearch]);

    const handleUserSelect = useCallback((user: GraphUser) => {
        setSelectedUser(user);
        setCreateError(null);
    }, []);

    const handleCreateProfile = useCallback(async () => {
        if (!selectedUser) {
            setCreateError("Please select a user first");
            return;
        }

        try {
            setCreateError(null);
            await createProfile({
                identity: selectedUser.displayName
            }).unwrap();

            // Close dialog and reset state
            closeDialog();

            // Navigate to ProfileEdit page with the new identity
            navigate(`/profile-edit?userName=${encodeURIComponent(selectedUser.displayName)}`);

            // Call callback if provided
            if (onProfileCreated) {
                onProfileCreated(selectedUser.displayName);
            }
        } catch (err: any) {
            console.error("Error creating profile:", err);
            setCreateError(err?.data?.message || "Failed to create profile. Please try again.");
        }
    }, [selectedUser, createProfile, closeDialog, navigate, onProfileCreated]);

    const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !isSearching && searchTerm.length >= 2) {
            handleSearch();
        }
    }, [isSearching, searchTerm, handleSearch]);

    return {
        open,
        openDialog,
        closeDialog,
        searchTerm,
        setSearchTerm,
        searchResults,
        isSearching,
        handleSearch,
        selectedUser,
        handleUserSelect,
        error,
        clearError,
        isCreating,
        handleCreateProfile,
        handleKeyPress
    };
};
